import React, { useContext, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Anchor, Button, Group, List, Paper, Radio, Stack, Text, Title, Tooltip } from "@mantine/core"
import { IconCheck, IconFileText, IconLink, IconMarkdown } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { LANDING_COPY } from "../../../docs/catalog"
import {
  PROCESSOR_STACKS,
  processorCoverPath,
  processorFullPath,
  type Processor
} from "../../../processors/catalog"
import { captureCopyAgentDirections, runAfterSuccessfulAction } from "../../../analytics"

type Copied = "link" | "directions" | null

// The two questions in front of a processor's directions: do you already take
// payments with it (the mode), and what is your server written in (the
// stack). Once both are answered it offers the same two copies as every other
// "Agent directions" control on the site: the link to the cover, or the full
// text. The text is assembled on the server, so it is fetched as soon as the
// answers are in, and the copy itself needs no network.
export const ProcessorWizard: React.FC<{ processor: Processor }> = observer(({ processor }) => {
  const workspace = useContext(AppWorkspaceContext)
  const copy = LANDING_COPY.processors
  const modes = processor.modes ?? []
  const [modeId, setModeId] = useState<string>(modes[0]?.id ?? "")
  const [stack, setStack] = useState<string | null>(null)
  const [text, setText] = useState<{ path: string; body: string } | null>(null)
  const [failed, setFailed] = useState(false)
  const [copied, setCopied] = useState<Copied>(null)

  const entry = PROCESSOR_STACKS.find((row) => row.stack === stack) ?? null
  const fullPath = entry ? processorFullPath(processor.id, modeId, entry.stack) : null
  const coverPath = entry ? processorCoverPath(processor.id, modeId, entry.stack) : null
  const ready = text !== null && text.path === fullPath

  useEffect(() => {
    if (!fullPath) return
    const controller = new AbortController()
    setFailed(false)
    fetch(fullPath, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`${fullPath}: ${response.status}`)
        return response.text()
      })
      .then((body) => setText({ path: fullPath, body }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error(error, { tags: { where: "ProcessorWizard" } })
          setFailed(true)
        }
      })
    return () => controller.abort()
  }, [fullPath])

  const write = async (kind: Exclude<Copied, null>, value: string) => {
    const succeeded = await runAfterSuccessfulAction(
      () => navigator.clipboard.writeText(value),
      () =>
        captureCopyAgentDirections({
          framework: entry?.framework ?? "",
          kind,
          ctaLocation: "processor_wizard"
        })
    )
    setCopied(succeeded ? kind : null)
    if (succeeded) window.setTimeout(() => setCopied(null), 1800)
  }

  const steps = copy.wizardSteps[modeId] ?? []

  return (
    <Paper className="or-processor-wizard" radius="lg" p={{ base: "md", sm: "xl" }} withBorder>
      <Stack gap="lg">
        <Title order={2} className="or-quicksetup-title">
          {copy.wizardTitle}
        </Title>

        <Radio.Group
          value={modeId}
          onChange={(value) => {
            setModeId(value)
            setCopied(null)
          }}
          label={copy.wizardModeQuestion.replace(/\{label\}/g, processor.label)}
        >
          <Stack gap="sm" mt="xs">
            {modes.map((mode) => (
              <Radio key={mode.id} value={mode.id} label={mode.label} description={mode.hint} />
            ))}
          </Stack>
        </Radio.Group>

        <div>
          <Text fw={500} size="sm" mb="xs">
            {copy.wizardStackQuestion}
          </Text>
          <Group gap="xs" wrap="wrap" role="group" aria-label={copy.wizardStackQuestion}>
            {PROCESSOR_STACKS.map((row) => {
              const selected = row.stack === stack
              return (
                <Button
                  key={row.stack}
                  variant={selected ? "filled" : "default"}
                  aria-pressed={selected}
                  onClick={() => {
                    setStack(row.stack)
                    setCopied(null)
                  }}
                >
                  {row.label}
                </Button>
              )
            })}
          </Group>
        </div>

        {entry && coverPath && fullPath ? (
          <Stack gap="sm" className="or-processor-directions">
            {steps.length ? (
              <List size="sm" spacing={4}>
                {steps.map((step) => (
                  <List.Item key={step}>{step}</List.Item>
                ))}
              </List>
            ) : null}
            <div className="or-copy-directions" role="group" aria-label="Agent directions">
              <span className="or-copy-directions-label">Agent directions · {entry.label}</span>
              <Button.Group className="or-copy-directions-actions">
                <Tooltip
                  label={copied === "link" ? "Link copied" : `Copy a link to ${coverPath}`}
                  withArrow
                  openDelay={200}
                >
                  <Button
                    className="or-copy-directions-link"
                    leftSection={copied === "link" ? <IconCheck size={18} /> : <IconLink size={18} />}
                    onClick={() => void write("link", workspace.absoluteUrl(coverPath))}
                  >
                    {copied === "link" ? "Link copied" : "Copy link"}
                  </Button>
                </Tooltip>
                <Tooltip
                  label={copied === "directions" ? "Text copied" : "Copy the full agent directions as text"}
                  withArrow
                  openDelay={200}
                >
                  <Button
                    className="or-copy-directions-directions"
                    variant="default"
                    disabled={!ready}
                    loading={!ready && !failed}
                    leftSection={copied === "directions" ? <IconCheck size={18} /> : <IconFileText size={18} />}
                    onClick={() => {
                      if (text) void write("directions", text.body)
                    }}
                  >
                    {copied === "directions" ? "Text copied" : "Copy text"}
                  </Button>
                </Tooltip>
              </Button.Group>
              <Button
                component="a"
                href={fullPath}
                variant="subtle"
                size="xs"
                leftSection={<IconMarkdown size={14} />}
                title="The full directions as raw markdown"
              >
                {ready ? `Markdown · ${Math.round(new Blob([text.body]).size / 1000)} KB` : "Markdown"}
              </Button>
            </div>
            {failed ? (
              <Text size="sm" c="red">
                Could not load the directions. Copy the link instead, or reload the page.
              </Text>
            ) : null}
            <Text size="sm" c="dimmed">
              {copy.wizardCopyHint}
            </Text>
          </Stack>
        ) : null}

        {/* For the people the two questions do not fit: a store plugin, or
            a server OpenReceive has no package for. */}
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {copy.woocommerce}{" "}
            <Anchor
              href="/integrations/woocommerce"
              onClick={(event) => {
                event.preventDefault()
                workspace.openHref("/integrations/woocommerce")
              }}
            >
              {copy.woocommerceLink}
            </Anchor>
          </Text>
          <Text size="sm" c="dimmed">
            {copy.unsupported}{" "}
            <Anchor
              href="/platforms"
              onClick={(event) => {
                event.preventDefault()
                workspace.goPlatforms()
              }}
            >
              {copy.unsupportedLink}
            </Anchor>
          </Text>
        </Stack>
      </Stack>
    </Paper>
  )
})
